import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  briefGetSession,
  briefTurn,
  briefTurnStream,
  type BriefTurnResponse,
  type BriefSessionResponse,
  type CustomerAction,
  type TurnShape,
  type BriefProgress,
} from '@ghostwright/api-client';
import ProgressDots from '../components/brief/ProgressDots';
import ClusterBand from '../components/brief/ClusterBand';
import AsyncNotice from '../components/brief/AsyncNotice';
import EditorTurn from '../components/brief/EditorTurn';
import CustomerTurn from '../components/brief/CustomerTurn';
import DraftCard from '../components/brief/DraftCard';
import MessageInput from '../components/brief/MessageInput';
import TransitionCard from '../components/brief/TransitionCard';
import ChipPick from '../components/brief/ChipPick';
import FreeTextWithExample from '../components/brief/FreeTextWithExample';
import BriefProgressDrawer from '../components/brief/BriefProgressDrawer';
import ThinkingIndicator from '../components/brief/ThinkingIndicator';
import FinalisingState from '../components/brief/FinalisingState';
import FailedState from '../components/brief/FailedState';

interface TranscriptEntry {
  role: 'user' | 'assistant';
  content: string;
}

type SendingState = 'idle' | 'streaming' | 'sending' | 'error';

export default function BriefIntake() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [lastTurnShape, setLastTurnShape] = useState<TurnShape | null>(null);
  const [progress, setProgress] = useState<BriefProgress | null>(null);
  const [status, setStatus] = useState<string>('active');
  const [loading, setLoading] = useState(true);
  const [sendingState, setSendingState] = useState<SendingState>('idle');
  const [lastError, setLastError] = useState<string | null>(null);
  const [judgeFeedback, setJudgeFeedback] = useState<string | undefined>();
  const [streamingText, setStreamingText] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [parkFlash, setParkFlash] = useState(false);

  const lastActionRef = useRef<CustomerAction | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const conversationEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sessionId) return;
    briefGetSession(sessionId)
      .then((session: BriefSessionResponse) => {
        setStatus(session.status);
        setProgress(session.progress);
        if (session.lastTurn) {
          setLastTurnShape(session.lastTurn.turnShape);
        }
        setTranscript(
          session.recentTranscript.map((t) => ({ role: t.role, content: t.content })),
        );
        setJudgeFeedback(session.judgeFeeback);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [sessionId]);

  useEffect(() => {
    if (conversationEndRef.current && !loading) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [transcript.length, sendingState, loading, streamingText]);

  const handleAction = useCallback(
    (action: CustomerAction) => {
      if (!sessionId || sendingState === 'streaming' || sendingState === 'sending') return;

      // Park feedback flash
      if (action.kind === 'park') {
        setParkFlash(true);
        setTimeout(() => setParkFlash(false), 2500);
      }

      setSendingState('streaming');
      setLastError(null);
      setStreamingText('');
      lastActionRef.current = action;

      const userContent =
        action.kind === 'message' ? action.text
        : action.kind === 'angle-chip' ? action.chipLabel
        : action.kind === 'edit-draft' ? action.text
        : action.kind === 'reject-draft' ? action.reason
        : action.kind === 'free-text-field' ? action.text
        : action.kind === 'chip-pick' ? `Selected: ${action.selectedIds.join(', ')}`
        : null;

      if (userContent) {
        setTranscript((prev) => [...prev, { role: 'user', content: userContent }]);
      }

      const controller = briefTurnStream(sessionId, action, {
        onDelta: (text) => {
          setStreamingText((prev) => prev + text);
        },
        onDone: (result) => {
          setStreamingText((current) => {
            setTranscript((prev) => [
              ...prev,
              { role: 'assistant', content: current },
            ]);
            return '';
          });
          setLastTurnShape(result.turnShape);
          setProgress(result.progress);
          setStatus(result.sessionStatus);
          setSendingState('idle');
          abortRef.current = null;
        },
        onError: () => {
          setStreamingText('');
          fallbackToSync(sessionId, action, userContent);
        },
      });

      abortRef.current = controller;
    },
    [sessionId, sendingState],
  );

  const fallbackToSync = useCallback(
    async (sid: string, action: CustomerAction, userContent: string | null) => {
      setSendingState('sending');
      try {
        const response: BriefTurnResponse = await briefTurn(sid, action);
        setTranscript((prev) => [
          ...prev,
          { role: 'assistant', content: response.turn.text },
        ]);
        setLastTurnShape(response.turn.turnShape);
        setProgress(response.progress);
        setStatus(response.status);
        setSendingState('idle');
      } catch (err) {
        if (userContent) {
          setTranscript((prev) => prev.slice(0, -1));
        }
        setLastError(
          err instanceof Error ? err.message : 'Something went wrong. Try again.',
        );
        setSendingState('error');
      }
    },
    [],
  );

  const handleRetry = useCallback(() => {
    if (lastActionRef.current) {
      setSendingState('idle');
      setLastError(null);
      handleAction(lastActionRef.current);
    }
  }, [handleAction]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
      </div>
    );
  }

  if (!sessionId) return null;

  if (status === 'finalising') {
    return (
      <BriefLayout progress={progress} onProgressClick={() => setDrawerOpen(true)}>
        <FinalisingState sessionId={sessionId} />
      </BriefLayout>
    );
  }

  if (status === 'failed') {
    return (
      <BriefLayout progress={progress} onProgressClick={() => setDrawerOpen(true)}>
        <FailedState feedback={judgeFeedback} onResume={() => setStatus('active')} />
      </BriefLayout>
    );
  }

  const isStreaming = sendingState === 'streaming' && streamingText.length > 0;
  const isWaiting = sendingState === 'streaming' && streamingText.length === 0;

  return (
    <BriefLayout progress={progress} onProgressClick={() => setDrawerOpen(true)}>
      <AsyncNotice />
      <div className="max-w-conversation mx-auto">
        {progress && (
          <ClusterBand
            clusterIndex={progress.clusterIndex}
            clusterName={progress.currentCluster}
          />
        )}

        {transcript.map((entry, i) =>
          entry.role === 'assistant' ? (
            <EditorTurn key={i} text={entry.content} />
          ) : (
            <CustomerTurn key={i} text={entry.content} />
          ),
        )}

        {isStreaming && <EditorTurn text={streamingText} streaming />}
        {(isWaiting || sendingState === 'sending') && <ThinkingIndicator />}

        {parkFlash && (
          <div className="mb-6 py-3 px-4 bg-card border border-border rounded-card text-sm text-muted-foreground animate-in fade-in duration-200">
            Parked — we'll circle back if there's time.
          </div>
        )}

        {sendingState === 'error' && lastError && (
          <div className="mb-8 p-4 bg-card border border-destructive/30 rounded-card">
            <p className="text-sm text-foreground mb-3">{lastError}</p>
            <button
              type="button"
              onClick={handleRetry}
              className="text-sm font-medium text-primary hover:underline"
            >
              Try again
            </button>
          </div>
        )}

        {sendingState === 'idle' && lastTurnShape && renderAffordance(lastTurnShape, handleAction)}

        {sendingState === 'error' && lastTurnShape && renderAffordance(lastTurnShape, handleAction)}

        <div ref={conversationEndRef} />
      </div>

      {progress?.clusterDetails && (
        <BriefProgressDrawer
          clusters={progress.clusterDetails}
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </BriefLayout>
  );
}

function renderAffordance(
  turnShape: TurnShape,
  handleAction: (action: CustomerAction) => void,
): React.ReactNode {
  switch (turnShape.kind) {
    case 'propose-to-react':
      return (
        <DraftCard
          draftTitle={turnShape.draftTitle ?? ''}
          draftBody={turnShape.draftBody ?? ''}
          draftMeta={turnShape.draftMeta}
          onAction={handleAction}
          disabled={false}
        />
      );
    case 'transition-acknowledge':
      return (
        <TransitionCard
          summary={turnShape.summary ?? ''}
          nextTopicHint={turnShape.nextTopicHint ?? ''}
          onAction={handleAction}
        />
      );
    case 'chip-pick-single':
      return (
        <ChipPick
          prompt={turnShape.prompt ?? ''}
          options={turnShape.options ?? []}
          mode="single"
          onAction={handleAction}
        />
      );
    case 'chip-pick-multi':
      return (
        <ChipPick
          prompt={turnShape.prompt ?? ''}
          options={turnShape.options ?? []}
          mode="multi"
          minSelect={turnShape.minSelect}
          maxSelect={turnShape.maxSelect}
          onAction={handleAction}
        />
      );
    case 'free-text-with-example':
      return (
        <FreeTextWithExample
          fieldLabel={turnShape.fieldLabel ?? ''}
          exampleText={turnShape.exampleText ?? ''}
          placeholder={turnShape.placeholder}
          onAction={handleAction}
        />
      );
    case 'closing':
      return null;
    case 'question':
    case 'reflection':
    default:
      return (
        <MessageInput
          angleChips={turnShape.kind === 'question' ? turnShape.angleChips : undefined}
          onAction={handleAction}
        />
      );
  }
}

function BriefLayout({
  children,
  progress,
  onProgressClick,
}: {
  children: React.ReactNode;
  progress: BriefProgress | null;
  onProgressClick?: () => void;
}) {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border">
        <div className="max-w-container mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <a href="/" className="font-display font-medium text-[22px] text-foreground tracking-[-0.005em] inline-flex items-center">
            <span translate="no">Ghostwright</span>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary ml-1.5 align-[0.15em]" aria-hidden="true" />
          </a>
          {progress && (
            <button
              type="button"
              onClick={onProgressClick}
              className="hover:opacity-80 transition-opacity"
              aria-label="View brief progress"
            >
              <ProgressDots progress={progress} />
            </button>
          )}
        </div>
      </header>
      <main className="flex-1 px-5 sm:px-8 py-8">{children}</main>
      <footer className="border-t border-border text-center py-6 text-xs text-placeholder">
        Ghostwright<span className="text-primary mx-1.5" aria-hidden="true">&#x25CF;</span>2026
      </footer>
    </div>
  );
}
